import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { useEvaluateThresholds, useNotifications } from '../../hooks/useNotifications';
import { DataTable, type Column } from '../../components/DataTable';
import { useToast } from '../../components/Toast';
import { fmtDateTime, fmtMonth, fmtUsd } from '../../lib/format';
import type { QueryOptions, ThresholdNotificationDto } from '../../lib/types';

export function Notifications() {
  const isAdmin = useIsAdmin();
  if (!isAdmin) {
    return (
      <div className="bg-white rounded-lg shadow border border-slate-200 p-6">
        <h1 className="text-xl font-bold text-atg-navy">Forbidden</h1>
        <p className="text-slate-600 mt-2">You need the <code>Admin</code> role to view this page.</p>
      </div>
    );
  }
  return <NotificationsInner />;
}

function NotificationsInner() {
  const [params] = useSearchParams();
  const groupId = params.get('groupId') ? Number(params.get('groupId')) : undefined;
  const [options, setOptions] = useState<QueryOptions>({ page: 1, pageSize: 25 });
  const q = useNotifications(options, groupId);
  const evaluate = useEvaluateThresholds();
  const toast = useToast();

  const columns: Column<ThresholdNotificationDto>[] = [
    { header: 'Sent', render: n => <span className="whitespace-nowrap">{fmtDateTime(n.sentAt)}</span> },
    { header: 'Group', render: n => <Link to={`/admin/groups/${n.groupId}`} className="text-atg-blue hover:underline">{n.groupName}</Link> },
    { header: 'Month', render: n => fmtMonth(n.month) },
    { header: 'What', render: n => n.kind === 'Status' ? <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700">status</span> : <span className="text-xs px-2 py-1 rounded bg-orange-100 text-orange-800">{n.thresholdPct}% alert</span> },
    { header: 'Spend / target', render: n => <span className="tabular-nums">{fmtUsd(n.mtdAtSend)}{n.targetAtSend !== null && <span className="text-slate-500"> / {fmtUsd(n.targetAtSend)}</span>}</span>, cellClass: 'text-right', headerClass: 'text-right' },
    { header: 'Recipients', render: n => <span className="text-xs text-slate-600" title={n.recipients.join(', ')}>{n.recipients.length}</span>, cellClass: 'text-right', headerClass: 'text-right' },
    { header: 'By', render: n => <span className="text-xs text-slate-600">{n.isManual ? n.triggeredBy : 'automatic'}</span> },
    { header: 'Result', render: n => n.error ? <span className="text-xs text-red-600">{n.error}</span> : <span className="text-xs text-emerald-700">sent{n.emailOperationId ? '' : ' (logged)'}</span> }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <div>
          <h1 className="text-2xl font-bold text-atg-navy">Notifications</h1>
          <p className="text-slate-600 mt-1 text-sm">
            Every threshold and status email, sent or attempted.{groupId ? <> Filtered to one group · <Link to="/admin/notifications" className="text-atg-blue hover:underline">show all</Link></> : ''}
          </p>
        </div>
        <button
          onClick={async () => {
            try {
              const r = await evaluate.mutateAsync();
              if (r.accepted.length > 0) toast.success('Threshold evaluation queued; new alerts appear here within a few seconds.');
              for (const rej of r.rejected) toast.warning(`${rej.job}: ${rej.reason}`);
            } catch { toast.error('Could not queue the evaluation.'); }
          }}
          disabled={evaluate.isPending}
          className="ml-auto rounded-md bg-atg-navy text-white px-4 py-2 text-sm font-medium hover:bg-atg-blue disabled:opacity-50"
        >
          Evaluate thresholds now
        </button>
      </div>

      <DataTable<ThresholdNotificationDto>
        data={q.data}
        isLoading={q.isLoading}
        error={q.error}
        options={options}
        onOptionsChange={setOptions}
        columns={columns}
        rowKey={n => n.id}
        searchable
        searchPlaceholder="Filter by group, month, recipient or who…"
        emptyState="No emails yet. Alerts fire automatically after each sync once a group crosses a threshold."
      />
    </div>
  );
}
